import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Users } from 'server/shared/collections/users';
import { userIncludesRequiredFieldsForJob } from 'server/shared/helpers/canBookInviteeForJob';
import { formatUserName } from 'server/shared/helpers/user/formatUserName';
import { CarLicenseEnum } from 'server/shared/models/carLicense.enum';
import { Customer } from 'server/shared/models/customer';
import { Job } from 'server/shared/models/job';
import { LanguageEnum } from 'server/shared/models/language.enum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { DispoUser, PoolerUser } from 'server/shared/models/user';
import {
  InviteeToggledEvent,
  JobMatchPoolerUser,
} from '../../../jobs/job-edit/job-edit.types';
import { PermissionsService } from '../../../services/permissions.service';
import { ToastService } from '../../../services/toast/toast.service';
import {
  RequiredFieldsDialog,
  RequiredFieldsDialogUpdatedFields,
} from '../../required-fields-dialog/required-fields.component';

let isEmpty = require('lodash/isEmpty');

@Component({
  selector: 'app-possible-user-list-item',
  templateUrl: 'job-possible-user-list-item.component.html',
  styleUrls: ['job-possible-user-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobPossibleUserListItemComponent implements OnInit {
  private _bemPrefix: string = 'user-list-item';

  customerUserInformationSubscription?: Subscription;
  roleEnum = RoleEnum;

  directAcceptChecked$ = new BehaviorSubject<boolean>(false);
  @Input() set directAcceptChecked(value: boolean | undefined) {
    this.directAcceptChecked$.next(value || false);
  }
  get directAcceptChecked() {
    return this.directAcceptChecked$.value;
  }

  directInviteChecked$ = new BehaviorSubject<boolean>(false);
  @Input() set directInviteChecked(value: boolean | undefined) {
    this.directInviteChecked$.next(value || false);
  }
  get directInviteChecked() {
    return this.directInviteChecked$.value;
  }

  user$ = new BehaviorSubject<JobMatchPoolerUser | undefined>(undefined);
  @Input() set user(value: JobMatchPoolerUser | undefined) {
    this.user$.next(value);
  }
  get user() {
    return this.user$.value;
  }

  dropdownMenu$ = new BehaviorSubject<MatMenu | undefined>(undefined);
  @Input() set dropdownMenu(value: MatMenu | undefined) {
    this.dropdownMenu$.next(value);
  }
  get dropdownMenu() {
    return this.dropdownMenu$.value;
  }

  job$ = new BehaviorSubject<Job | undefined>(undefined);
  @Input() set job(value: Job | undefined) {
    this.job$.next(value);
  }
  get job() {
    return this.job$.value;
  }

  jobDate$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set jobDate(value: string | undefined) {
    this.jobDate$.next(value);
  }
  get jobDate() {
    return this.jobDate$.value;
  }

  randomId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set randomId(value: string | undefined) {
    this.randomId$.next(value);
  }
  get randomId() {
    return this.randomId$.value;
  }

  jobCount$ = new BehaviorSubject<number | undefined>(undefined);
  @Input() set jobCount(value: number | undefined) {
    this.jobCount$.next(value);
  }
  get jobCount() {
    return this.jobCount$.value;
  }

  userCount$ = new BehaviorSubject<Map<string, number> | undefined>(undefined);
  @Input() set userCount(value: Map<string, number> | undefined) {
    this.userCount$.next(value);
  }
  get userCount() {
    return this.userCount$.value;
  }

  jobInPast$ = new BehaviorSubject<boolean>(false);
  @Input() set jobInPast(value: boolean) {
    this.jobInPast$.next(value);
  }
  get jobInPast() {
    return this.jobInPast$.value;
  }

  jobCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);
  @Input() set jobCustomer(value: Customer | undefined) {
    this.jobCustomer$.next(value);
  }
  get jobCustomer() {
    return this.jobCustomer$.value;
  }

  loggedInCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);
  @Input() set loggedInCustomer(value: Customer | undefined) {
    this.loggedInCustomer$.next(value);
  }
  get loggedInCustomer() {
    return this.loggedInCustomer$.value;
  }

  @Output() inviteeToggled = new EventEmitter<InviteeToggledEvent>();

  animationShowDetails$ = new BehaviorSubject<boolean>(false);
  showDetails$ = new BehaviorSubject<boolean>(false);
  carLicenseEnum = CarLicenseEnum;
  languageEnum = LanguageEnum;
  sexEnum = SexEnum;

  inviteeNote$ = new BehaviorSubject<string | undefined>(undefined);

  userSubscription?: Subscription;

  isUndecided$ = new BehaviorSubject<boolean>(false);

  loggedInUser$ = new BehaviorSubject<DispoUser | undefined>(undefined);

  constructor(
    private ngZone: NgZone,
    public permissionsService: PermissionsService,
    private route: ActivatedRoute,
    private bottomSheet: MatBottomSheet,
    private dialog: MatDialog,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    if (this.directInviteChecked) {
      this.inviteCheckboxChange();
    }
    if (this.directAcceptChecked) {
      this.directAcceptCheckboxChange();
    }
    Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.loggedInUser$.next(Users.findOne(Meteor.userId()) as DispoUser);
      });
    });
    if (!this.user || !this.job) return;
    this.isUndecided$.next(this.user.isUndecided);
  }

  ngOnDestroy() {
    if (this.customerUserInformationSubscription) {
      this.customerUserInformationSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  get countAvailableForJobs(): number {
    const user = this.user$.value;
    const userCount = this.userCount$.value;
    if (!user) {
      return 0;
    }
    return userCount?.get(user._id) || 0;
  }

  canBookDirectly(): boolean {
    const user = this.user$.value;
    const isUndecided = this.isUndecided$.value;
    const jobInPast = this.jobInPast$.value;
    return (user?.canBeBookedDirectly && !isUndecided) || jobInPast;
  }

  inviteCheckboxChange(restricted = false) {
    let user = this.user as PoolerUser;
    const jobCustomer = this.jobCustomer$.value;
    if (!jobCustomer || !this.user) {
      return;
    }

    if (
      !userIncludesRequiredFieldsForJob(user, jobCustomer) &&
      !this.directInviteChecked
    ) {
      const dialogRef = this.dialog.open(RequiredFieldsDialog, {
        data: {
          user,
          requiredFields: jobCustomer?.requiredJobFields,
        },
      });

      dialogRef
        .afterClosed()
        .subscribe((result: RequiredFieldsDialogUpdatedFields) => {
          if (result) {
            Meteor.call(
              'updateRequiredFields',
              user._id,
              result,
              (error: Meteor.Error) => {
                if (error) {
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                  this.directAcceptChecked$.next(false);
                  return;
                } else {
                  setTimeout(() => {
                    this.directAcceptCheckboxChange();
                  }, 200);
                }
              },
            );
          } else {
            this.directAcceptChecked$.next(false);
          }
        });
      return;
    }

    if (this.directAcceptChecked) this.directAcceptChecked$.next(false);
    if (restricted && this.user?.restWarning) {
      return;
    }
    if (restricted && this.user?.availabilityNote) {
      return;
    }
    this.inviteeToggled.emit({
      user: this.user,
      inviteeSelected: this.directInviteChecked || false,
      type: 'INVITE',
    });
  }

  directAcceptCheckboxChange() {
    let user = this.user as PoolerUser;
    const jobCustomer = this.jobCustomer$.value;
    if (!jobCustomer || !this.user) {
      return;
    }
    if (
      !userIncludesRequiredFieldsForJob(user, jobCustomer) &&
      !this.directInviteChecked
    ) {
      const dialogRef = this.dialog.open(RequiredFieldsDialog, {
        data: {
          user,
          requiredFields: jobCustomer?.requiredJobFields,
        },
      });

      dialogRef
        .afterClosed()
        .subscribe((result: RequiredFieldsDialogUpdatedFields) => {
          if (result) {
            Meteor.call(
              'updateRequiredFields',
              user._id,
              result,
              (error: Meteor.Error) => {
                if (error) {
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                  this.directAcceptChecked$.next(false);
                  return;
                } else {
                  setTimeout(() => {
                    this.directAcceptCheckboxChange();
                  }, 200);
                }
              },
            );
          } else {
            this.directAcceptChecked$.next(false);
          }
        });
      return;
    }
    if (this.directInviteChecked) this.directInviteChecked$.next(false);
    this.inviteeToggled.emit({
      user: this.user,
      inviteeSelected: this.directAcceptChecked || false,
      type: 'DIRECT_ACCEPT',
    });
  }

  @HostListener('click', ['$event'])
  toggleUserDetails(event: Event) {
    this.toggleDetails(event);
  }

  toggleDetails(event: Event) {
    event.preventDefault();
    if (this.showDetails$.value) {
      this.animationShowDetails$.next(false);
      // un-render elements after animation delay
      setTimeout(() => {
        this.showDetails$.next(false);
      }, 1000);
    } else {
      this.showDetails$.next(true);
      // start animation immediately after rendering elements
      setTimeout(() => {
        this.animationShowDetails$.next(true);
      });
    }
  }

  formatUserName(): string {
    const user = this.user$.value;
    const jobCustomer = this.jobCustomer$.value;
    if (!user) return '';
    return formatUserName(user, jobCustomer);
  }
}
